import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  MessageSquare,
  Bug,
  Lightbulb,
  Heart,
  HelpCircle,
  MoreHorizontal,
  Star,
  RefreshCw,
  CheckCircle,
  Clock,
  Eye,
  Trash2,
  ExternalLink,
  User,
  Calendar,
  Filter
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TYPE_CONFIG = {
  bug: { label: 'Feil/Bug', icon: Bug, color: 'bg-red-600', textColor: 'text-red-400' },
  suggestion: { label: 'Forslag', icon: Lightbulb, color: 'bg-yellow-600', textColor: 'text-yellow-400' },
  praise: { label: 'Ros', icon: Heart, color: 'bg-pink-600', textColor: 'text-pink-400' },
  question: { label: 'Spørsmål', icon: HelpCircle, color: 'bg-blue-600', textColor: 'text-blue-400' },
  other: { label: 'Annet', icon: MoreHorizontal, color: 'bg-gray-600', textColor: 'text-gray-400' }
};

const STATUS_CONFIG = {
  new: { label: 'Ny', color: 'bg-purple-600' },
  reviewed: { label: 'Gjennomgått', color: 'bg-blue-600' },
  in_progress: { label: 'Under arbeid', color: 'bg-yellow-600' },
  resolved: { label: 'Løst', color: 'bg-green-600' },
  closed: { label: 'Lukket', color: 'bg-gray-600' }
};

const AdminFeedback = () => {
  const { token } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.feedback_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;

      const [feedbackRes, statsRes] = await Promise.all([
        axios.get(`${API}/feedback`, { params, headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/feedback/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setFeedback(feedbackRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filterType, filterStatus]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const handleStatusUpdate = async (feedbackId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/feedback/${feedbackId}/status`, {
        status: newStatus,
        admin_notes: adminNotes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchFeedback();
      setSelectedFeedback(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error updating feedback:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne tilbakemeldingen?')) return;

    try {
      await axios.delete(`${API}/feedback/${feedbackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFeedback();
      setSelectedFeedback(null);
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nb-NO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.other;
  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.new;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Tilbakemeldinger</h1>
          <p className="text-gray-400 mt-1">Feedback fra brukere i testfasen</p>
        </div>
        <Button
          onClick={fetchFeedback}
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-800"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Oppdater
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-purple-900/30 border-purple-600">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-white">{stats.by_status.new}</div>
              <div className="text-purple-400 text-sm">Nye</div>
            </CardContent>
          </Card>
          <Card className="bg-red-900/30 border-red-600">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-white">{stats.by_type.bug}</div>
              <div className="text-red-400 text-sm">Feil</div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-900/30 border-yellow-600">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-white">{stats.by_type.suggestion}</div>
              <div className="text-yellow-400 text-sm">Forslag</div>
            </CardContent>
          </Card>
          <Card className="bg-pink-900/30 border-pink-600">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-white">{stats.by_type.praise}</div>
              <div className="text-pink-400 text-sm">Ros</div>
            </CardContent>
          </Card>
          <Card className="bg-green-900/30 border-green-600">
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-white">{stats.by_status.resolved}</div>
              <div className="text-green-400 text-sm">Løst</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="text-gray-400 text-sm block mb-1">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
              >
                <option value="all">Alle typer</option>
                {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-sm block mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white"
              >
                <option value="all">Alle statuser</option>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* List */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Tilbakemeldinger ({feedback.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto">
            {feedback.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Ingen tilbakemeldinger ennå</p>
              </div>
            ) : (
              <div className="space-y-3">
                {feedback.map((item) => {
                  const typeConfig = getTypeConfig(item.feedback_type);
                  const statusConfig = getStatusConfig(item.status);
                  const TypeIcon = typeConfig.icon;

                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        setSelectedFeedback(item);
                        setAdminNotes(item.admin_notes || '');
                      }}
                      className={`p-4 rounded-lg cursor-pointer transition-all ${
                        selectedFeedback?.id === item.id
                          ? 'bg-purple-900/30 border border-purple-600'
                          : 'bg-gray-900/50 hover:bg-gray-900 border border-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                            <TypeIcon className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                              {item.rating > 0 && (
                                <div className="flex items-center gap-0.5">
                                  {[...Array(item.rating)].map((_, i) => (
                                    <Star key={i} className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                  ))}
                                </div>
                              )}
                            </div>
                            <p className="text-white text-sm line-clamp-2">{item.message}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                              {item.user_email && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {item.user_email}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(item.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail View */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Detaljer
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedFeedback ? (
              <div className="space-y-4">
                {/* Type & Status */}
                <div className="flex items-center gap-3">
                  {(() => {
                    const config = getTypeConfig(selectedFeedback.feedback_type);
                    const Icon = config.icon;
                    return (
                      <Badge className={config.color}>
                        <Icon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Badge>
                    );
                  })()}
                  <Badge className={getStatusConfig(selectedFeedback.status).color}>
                    {getStatusConfig(selectedFeedback.status).label}
                  </Badge>
                </div>

                {/* Message */}
                <div>
                  <label className="text-gray-400 text-sm">Melding</label>
                  <p className="text-white mt-1 bg-gray-900 p-3 rounded-lg">
                    {selectedFeedback.message}
                  </p>
                </div>

                {/* Rating */}
                {selectedFeedback.rating > 0 && (
                  <div>
                    <label className="text-gray-400 text-sm">Vurdering</label>
                    <div className="flex items-center gap-1 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < selectedFeedback.rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* User Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-sm">Bruker</label>
                    <p className="text-white mt-1">
                      {selectedFeedback.user_name || selectedFeedback.user_email || 'Anonym'}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-sm">Rolle</label>
                    <p className="text-white mt-1">
                      {selectedFeedback.user_role || 'Besøkende'}
                    </p>
                  </div>
                </div>

                {/* Page URL */}
                {selectedFeedback.page_url && (
                  <div>
                    <label className="text-gray-400 text-sm">Side</label>
                    <a
                      href={selectedFeedback.page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 mt-1 flex items-center gap-1 text-sm"
                    >
                      {selectedFeedback.page_url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}

                {/* Date */}
                <div>
                  <label className="text-gray-400 text-sm">Mottatt</label>
                  <p className="text-white mt-1">{formatDate(selectedFeedback.created_at)}</p>
                </div>

                {/* Admin Notes */}
                <div>
                  <label className="text-gray-400 text-sm">Admin-notater</label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Legg til notater..."
                    className="mt-1 bg-gray-900 border-gray-600 text-white"
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-700">
                  <Button
                    onClick={() => handleStatusUpdate(selectedFeedback.id, 'reviewed')}
                    disabled={updating}
                    variant="outline"
                    className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Gjennomgått
                  </Button>
                  <Button
                    onClick={() => handleStatusUpdate(selectedFeedback.id, 'in_progress')}
                    disabled={updating}
                    variant="outline"
                    className="border-yellow-600 text-yellow-400 hover:bg-yellow-900/30"
                  >
                    <Clock className="w-4 h-4 mr-1" />
                    Under arbeid
                  </Button>
                  <Button
                    onClick={() => handleStatusUpdate(selectedFeedback.id, 'resolved')}
                    disabled={updating}
                    variant="outline"
                    className="border-green-600 text-green-400 hover:bg-green-900/30"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Løst
                  </Button>
                  <Button
                    onClick={() => handleDelete(selectedFeedback.id)}
                    variant="outline"
                    className="border-red-600 text-red-400 hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Slett
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Eye className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Velg en tilbakemelding for å se detaljer</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminFeedback;
